import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useSiteConfig } from "../../context/SiteConfigContext";
import { useCart } from "../../context/CartContext";

import { Heart, ArrowRight } from "lucide-react";

import { showError } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";

function Card({ item }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { config } = useSiteConfig();

  const initialPrice =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.price ?? 0)
      : (item?.price ?? 0);

  const initialMrp =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.mrp ?? 0)
      : (item?.mrp ?? 0);

  const initialDiscount =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.discount ?? 0)
      : (item?.discount ?? 0);

  const initialStock =
    item?.productType === "Variant"
      ? (item?.variants?.[0]?.stock ?? 0)
      : (item?.stock ?? 0);

  const [image, setImage] = useState(item?.thumbnail);
  const [slug] = useState(item?.slug);
  const [title] = useState(item?.title);
  const [category] = useState(item?.category);

  const [activePrice, setActivePrice] = useState(initialPrice);
  const [activeMrp, setActiveMrp] = useState(initialMrp);
  const [activeDiscount, setActiveDiscount] =
    useState(initialDiscount);

  const [activeStock, setActiveStock] =
    useState(initialStock);

  const [activeVariantId, setActiveVariantId] =
    useState(
      item?.productType === "Variant" &&
      item?.variants?.length
        ? item?.variants[0]._id
        : null
    );

  const [isWishlisted, setIsWishlisted] =
    useState(item?.isWishlisted || false);


  /* =========================================
     UPDATE VARIANT
  ========================================= */

  useEffect(() => {
    if (item?.productType !== "Variant") return;

    const selected = item?.variants?.find(
      (v) => v._id === activeVariantId
    );

    if (selected) {
      const newImg =
        selected.images?.[0] || item?.thumbnail;

      setImage(newImg);
      setActivePrice(selected.price);
      setActiveMrp(selected.mrp);
      setActiveDiscount(selected.discount);
      setActiveStock(selected.stock);
    }
  }, [activeVariantId, item]);


  /* =========================================
     DISCOUNT
  ========================================= */

  const hasDiscount =
    activeDiscount !== null &&
    activeDiscount > 0 &&
    activeMrp > activePrice;


  /* =========================================
     ADD TO CART
  ========================================= */

  const handleAddToCart = (e) => {
    const action =
      e.currentTarget.getAttribute("data-action");

    try {
      addToCart(
        item?._id,
        1,
        activeVariantId || ""
      );

      if (action === "buy-now") {
        navigate("/checkout");
      }

    } catch (err) {
      console.error(
        "Add to cart error:",
        err
      );
    }
  };


  /* =========================================
     WISHLIST
  ========================================= */

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError(
        "Please log in to manage your wishlist"
      );
      return;
    }

    try {
      const res =
        await wishlistAPI.addProduct({
          productId: item?._id,
        });

      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }

    } catch (error) {
      showError(
        "Failed to update wishlist"
      );
    }
  };


  const isOutOfStock =
    activeStock <= 0;


  return (
    <div className="custom-product-card">

      {/* =====================================
          IMAGE AREA
      ===================================== */}

      <div className="custom-product-image">

        {/* Badge */}
        {hasDiscount && (
          <span className="custom-product-badge">
            {Math.round(activeDiscount)}% OFF
          </span>
        )}

        {/* Wishlist */}
        <button
          type="button"
          className={`custom-wishlist-btn ${
            isWishlisted ? "active" : ""
          }`}
          onClick={handleWishlistToggle}
          aria-label="Wishlist"
        >
          <Heart
            size={19}
            className="heart-icon"
            fill={
              isWishlisted
                ? "currentColor"
                : "none"
            }
          />
        </button>


        {/* Product Image */}

        <Link
          to={`/shop/${slug}`}
          className="custom-product-image-link"
        >
          <div className="custom-product-image-glow"></div>

          <img
            src={image}
            alt={title || "Product"}
          />
        </Link>

      </div>


      {/* =====================================
          PRODUCT CONTENT
      ===================================== */}

      <div className="custom-product-content">

        <Link
          to={`/shop/${slug}`}
          className="text-decoration-none"
        >
          <h3 className="m-0">{title}</h3>
        </Link>


        <p className="custom-product-category">
          {category}
        </p>


        {/* Rating */}

        <div className="custom-product-rating">

          <span className="custom-rating-stars">
            ★★★★★
          </span>

          <span className="custom-rating-text">
            4.8
          </span>

        </div>


        {/* Price */}

        <div className="custom-product-price-row">

          <div className="custom-product-price">

            <span className="custom-price">
              ₹{activePrice?.toLocaleString("en-IN")}
            </span>

            {activeMrp > activePrice && (
              <span className="custom-old-price">
                ₹{activeMrp?.toLocaleString("en-IN")}
              </span>
            )}

          </div>

          {hasDiscount && (
            <span className="custom-discount">
              {Math.round(activeDiscount)}% OFF
            </span>
          )}

        </div>


        {/* Add To Cart */}

        {!isOutOfStock &&
          config?.productVisibility === "cart" && (

            <button
              className="custom-add-btn"
              data-id={item?._id}
              data-price={activePrice}
              data-variant={
                activeVariantId || ""
              }
              data-quantity={1}
              data-action="cart"
              data-bs-toggle="offcanvas"
              data-bs-target="#shoppingCartCanvas"
              onClick={handleAddToCart}
            >

              <span>
                ADD TO CART
              </span>

              <ArrowRight size={18} />

            </button>

          )}


        {/* Out Of Stock */}

        {isOutOfStock && (
          <div className="custom-out-stock">
            OUT OF STOCK
          </div>
        )}

      </div>

    </div>
  );
}

export default Card;